import { Product, ProductVariant } from '@shopify/hydrogen-react/storefront-api-types';
import React, { createContext, useState, useRef, ReactNode, useEffect } from 'react';
import { useStoreState } from '../../store';
import { elevarSearchImpressions, elevarViewProductsList } from '../../utils/elevar';
import useIntersectionObserver from '../../utils/useIntersectionObserver';

// Define the type for the context value
interface ProductContextType {
  products: { productVariant: ProductVariant; product: Product }[];
  addProduct: (
    product: { productVariant: ProductVariant; product: Product },
    index: number
  ) => void;
}

// Define the type for the provider props
interface ProductProviderProps {
  innerWrapperClassName?: string;
  children: ReactNode;
  isSearchPage?: boolean;
  initialProducts?: { productVariant: ProductVariant; product: Product }[];
}

// Create the context
const ViewedProductContext = createContext<ProductContextType | undefined>(undefined);

export const ViewedProductsProvider: React.FC<ProductProviderProps> = ({
  children,
  initialProducts = [],
  innerWrapperClassName,
  isSearchPage = false,
}) => {
  const sectionRef = useRef(null);
  const [products, setProducts] =
    useState<{ productVariant: ProductVariant; product: Product }[]>(initialProducts);
  const [isInView, setIsInView] = useState(false);
  const { isElevarInitializationFinished, elevarCustomerData } = useStoreState(
    store => store.analytics
  );

  // Function to add a product at a specific index
  const addProduct = (
    newProduct: { productVariant: ProductVariant; product: Product },
    index: number
  ) => {
    setProducts(prevProducts => {
      const productExists = prevProducts.some(
        product => product.productVariant.sku === newProduct.productVariant.sku
      );
      if (productExists) {
        return prevProducts;
      }
      const newProducts = [...prevProducts];
      newProducts.splice(index, 0, newProduct);
      return newProducts;
    });
  };

  useIntersectionObserver(sectionRef, entry => {
    setIsInView(entry.isIntersecting);
  });

  useEffect(() => {
    if (isElevarInitializationFinished && !isSearchPage && isInView && products.length > 0) {
      elevarViewProductsList(elevarCustomerData, products);
    }
  }, [isInView, products, isElevarInitializationFinished]);

  useEffect(() => {
    if (isElevarInitializationFinished && isSearchPage && products.length > 0) {
      elevarSearchImpressions(elevarCustomerData, products);
    }
  }, [products, isElevarInitializationFinished]);

  return (
    <div className={innerWrapperClassName} ref={sectionRef}>
      <ViewedProductContext.Provider value={{ products, addProduct }}>
        {children}
      </ViewedProductContext.Provider>
    </div>
  );
};
export default ViewedProductContext;
